import {
  Form,
  Grid,
  Input,
  Panel,
  SingleColumnLayout,
  SelectMulti,
  ISelectMultiOption,
  RadioButton,
  Box,
  P,
} from '@salecycle/ui-component-library';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { updateStepValidityAction } from '../../actions';
import { useNewSftpUserDispatch, useNewSftpUserState } from '../../context/useNewSftpUserContext';
import { usernameValidCheck, emailValidCheck } from 'sections/user-management/utils/helpers';
import { useClients } from 'core/app/AppContext';
import { Brand, Group, Site } from 'sections/client-management/entities';
import { entityToSortedSelectOptions } from 'components/utils/getSelectOptions';

export interface IProps {
  currentStep: number;
}

/* eslint-disable react-hooks/exhaustive-deps */
export const AddSftpUserStep = ({ currentStep }: IProps) => {
  const { t } = useTranslation(['common', 'sftpUserManagement']);
  const { validSteps, sftpUser } = useNewSftpUserState();
  const dispatch = useNewSftpUserDispatch();
  const [nameValid, setNameValid] = useState(usernameValidCheck(sftpUser.username));
  const [nameTouched, setNameTouched] = useState(sftpUser.username.length !== 0);

  const [emailValid, setEmailValid] = useState(emailValidCheck(sftpUser.email));
  const [emailTouched, setEmailTouched] = useState(sftpUser.email.length !== 0);

  const [userType, setUserType] = useState('external');

  const { getSites, getBrands, getGroups } = useClients();
  const sites: ISelectMultiOption<string>[] = entityToSortedSelectOptions<Site>(getSites());
  const brands: ISelectMultiOption<string>[] = entityToSortedSelectOptions<Brand>(getBrands());
  const groups: ISelectMultiOption<string>[] = entityToSortedSelectOptions<Group>(getGroups());

  useEffect(() => {
    const valid = nameValid && emailValid;
    updateStepValidityAction(valid, validSteps, currentStep, dispatch);
  }, [nameValid, emailValid]);

  const handleNameChange = (value: string) => {
    setNameValid(usernameValidCheck(value));

    if (nameTouched === false) {
      setNameTouched(true);
    }

    dispatch({ type: 'UPDATE_SFTP_USER_NAME', payload: { username: value } });
  };

  const handleEmailChange = (value: string) => {
    setEmailValid(emailValidCheck(value));

    if (emailTouched === false) {
      setEmailTouched(true);
    }

    dispatch({ type: 'UPDATE_SFTP_USER_EMAIL', payload: { email: value } });
  };

  const getClientSelectOptions = (type: string): ISelectMultiOption<string>[] => {
    switch (type) {
      case 'sites':
        return sites;
      case 'brands':
        return brands;
      case 'groups':
        return groups;
    }
  };

  const getClientChangeHandler = (type: string) => {
    const actionTypes = {
      sites: 'UPDATE_SITES',
      brands: 'UPDATE_BRANDS',
      groups: 'UPDATE_GROUPS',
    };
    return (clients: ISelectMultiOption<string>[]) => {
      return dispatch({
        type: actionTypes[type],
        payload: { [type]: clients },
      });
    };
  };

  const handleUserTypeChange = (userType: string) => {
    sftpUser.userType = userType;
    setUserType(userType);
  };

  return (
    <SingleColumnLayout
      paddingLeft={20}
      heading={t('sftpUserManagement:newSftpUser.addSftpUserStep.heading')}
      showSidebar={false}
    >
      <Grid>
        <Panel heading={t('sftpUserManagement:newSftpUser.addSftpUserStep.panelHeading')}>
          <Form>
            <Input
              label={t('sftpUserManagement:newSftpUser.addSftpUserStep.username.label')}
              ariaLabel={t('sftpUserManagement:newSftpUser.addSftpUserStep.username.label')}
              placeholder={t('sftpUserManagement:newSftpUser.addSftpUserStep.username.placeholder')}
              validationState={nameTouched && nameValid ? 'complete' : nameTouched ? 'warning' : null}
              validationMessage={
                nameTouched && !nameValid
                  ? t('sftpUserManagement:newSftpUser.addSftpUserStep.username.validation')
                  : null
              }
              onChange={handleNameChange}
              value={sftpUser.username}
            />

            <Input
              label={t('sftpUserManagement:newSftpUser.addSftpUserStep.email.label')}
              ariaLabel={t('sftpUserManagement:newSftpUser.addSftpUserStep.email.label')}
              inputId="EmailInput"
              type="email"
              placeholder={t('sftpUserManagement:newSftpUser.addSftpUserStep.email.placeholder')}
              validationState={emailTouched && emailValid ? 'complete' : emailTouched ? 'warning' : null}
              validationMessage={
                emailTouched && !emailValid
                  ? t('sftpUserManagement:newSftpUser.addSftpUserStep.email.validation')
                  : null
              }
              onChange={handleEmailChange}
              value={sftpUser.email}
              tooltipHeading={t('sftpUserManagement:newSftpUser.addSftpUserStep.email.label')}
              tooltipDescription={t('sftpUserManagement:userManagement.sftpUsers.tooltips.email')}
            />

            <P>{t('sftpUserManagement:sftpUsers.userType.userType')}</P>
            <Box margin="0 0 20px 0">
              <RadioButton
                label={t('sftpUserManagement:sftpUsers.userType.internal')}
                selected={userType === 'internal'}
                onClick={() => handleUserTypeChange('internal')}
              />
              <RadioButton
                label={t('sftpUserManagement:sftpUsers.userType.external')}
                selected={userType === 'external'}
                onClick={() => handleUserTypeChange('external')}
              />
            </Box>

            {['groups', 'brands', 'sites'].map(type => (
              <SelectMulti
                key={type}
                label={t(`sftpUserManagement:newSftpUser.addSftpUserStep.${type}.label`)}
                ariaLabel={t(`sftpUserManagement:newSftpUser.addSftpUserStep.${type}.label`)}
                inputId={`${type}-input`}
                placeholder={t(`sftpUserManagement:newSftpUser.addSftpUserStep.${type}.placeholder`)}
                value={sftpUser[type]}
                options={getClientSelectOptions(type)}
                onChange={getClientChangeHandler(type)}
                minLength={3}
                minLengthPlaceholder={t('common:common.minLengthPlaceholder', {
                  minLength: 3,
                })}
                noOptionsPlaceholder={t('common:common.noOptionsPlaceholder')}
              />
            ))}
          </Form>
        </Panel>
      </Grid>
    </SingleColumnLayout>
  );
};

export default AddSftpUserStep;
